import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContactBulkGroupsMode, ContactGroupSummary } from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import { AlertDialog, AlertDialogDescription, AlertDialogTitle } from "../../../components/ui/alert-dialog/index.js";
import {
  contactsModalBodyCompactClass,
  contactsModalFooterCompactClass,
  contactsModalHeaderCompactClass,
} from "../contacts-dialog-presentation.js";
import { ContactsModalContent } from "../ContactsModalContent.js";
import { ContactGroupMultiSelect } from "../ContactGroupMultiSelect/index.js";
import {
  contactGroupSelectionToRequest,
  emptyContactGroupSelection,
  type ContactGroupSelectionValue,
} from "../contact-group-selection-value.js";

export function ContactsBulkGroupDialog({
  open,
  mode,
  groups,
  selectedCount,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean;
  mode: ContactBulkGroupsMode;
  groups: ContactGroupSummary[];
  selectedCount: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: (selection: { groupIds: number[]; newGroupNames: string[] }) => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<ContactGroupSelectionValue>(emptyContactGroupSelection());
  const [error, setError] = useState<string | null>(null);
  const allowCreateNewGroup = mode === "add" || mode === "replace";

  useEffect(() => {
    if (open) {
      setSelection(emptyContactGroupSelection());
      setError(null);
    }
  }, [open, mode]);

  const titleKey =
    mode === "add"
      ? "contacts.bulk.addToGroupTitle"
      : mode === "remove"
        ? "contacts.bulk.removeFromGroupTitle"
        : mode === "replace"
          ? "contacts.bulk.replaceGroupsTitle"
          : "contacts.bulk.clearGroupsTitle";

  const descriptionKey =
    mode === "add"
      ? "contacts.bulk.addToGroupDescription"
      : mode === "remove"
        ? "contacts.bulk.removeFromGroupDescription"
        : mode === "replace"
          ? "contacts.bulk.replaceGroupsDescription"
          : "contacts.bulk.clearGroupsDescription";

  const pickerLabels =
    mode === "remove"
      ? {
          existingLabel: t("contacts.bulk.removeExistingLabel"),
          existingTrigger: t("contacts.bulk.removeExisting"),
          createSectionLabel: undefined,
          createPlaceholder: undefined,
        }
      : mode === "replace"
        ? {
            existingLabel: t("contacts.bulk.replaceExistingLabel"),
            existingTrigger: t("contacts.bulk.replaceExisting"),
            createSectionLabel: t("contacts.bulk.replaceCreateLabel"),
            createPlaceholder: t("contacts.bulk.replaceCreatePlaceholder"),
          }
        : {
            existingLabel: t("contacts.bulk.addExistingLabel"),
            existingTrigger: t("contacts.bulk.addExisting"),
            createSectionLabel: t("contacts.bulk.addCreateLabel"),
            createPlaceholder: t("contacts.bulk.addCreatePlaceholder"),
          };

  const handleConfirm = async () => {
    if (mode === "clear") {
      setError(null);
      await onConfirm({ groupIds: [], newGroupNames: [] });
      return;
    }
    const payload = contactGroupSelectionToRequest(selection);
    const groupCount = (payload.groupIds?.length ?? 0) + (payload.newGroupNames?.length ?? 0);
    if (groupCount === 0) {
      setError(t("contacts.bulk.groupsRequired"));
      return;
    }
    if (mode === "remove" && (payload.newGroupNames?.length ?? 0) > 0) {
      setError(t("contacts.bulk.removeExistingOnly"));
      return;
    }
    setError(null);
    await onConfirm({
      groupIds: payload.groupIds ?? [],
      newGroupNames: allowCreateNewGroup ? (payload.newGroupNames ?? []) : [],
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onClose()}>
      <ContactsModalContent className="w-[min(92vw,28rem)]">
        <div className={contactsModalHeaderCompactClass}>
          <AlertDialogTitle className="text-[0.9375rem] font-semibold tracking-tight">
            {t(titleKey, { count: selectedCount })}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[0.8125rem] leading-snug">
            {t(descriptionKey)}
          </AlertDialogDescription>
        </div>
        {mode === "clear" ? null : (
          <div className={contactsModalBodyCompactClass}>
            <ContactGroupMultiSelect
              groups={groups}
              value={selection}
              onChange={setSelection}
              disabled={busy}
              presentation="section"
              allowCreateNewGroup={allowCreateNewGroup}
              existingLabel={pickerLabels.existingLabel}
              existingTrigger={pickerLabels.existingTrigger}
              createSectionLabel={pickerLabels.createSectionLabel}
              createPlaceholder={pickerLabels.createPlaceholder}
              emptyHint={
                mode === "remove" ? t("contacts.bulk.removeGroupsEmptyHint") : undefined
              }
            />
            {error ? <p className="mt-2 text-[0.8125rem] text-red-600">{error}</p> : null}
          </div>
        )}
        <div className={contactsModalFooterCompactClass}>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "clear" ? "destructive" : "default"}
            disabled={busy}
            onClick={() => void handleConfirm()}
            data-testid="contacts-bulk-groups-confirm"
          >
            {t("common.apply")}
          </Button>
        </div>
      </ContactsModalContent>
    </AlertDialog>
  );
}
